import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Blade from '@/components/ui/Blade/Blade';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { PaymentDeviceRentalStatus } from '@/models/common/PaymentDeviceEnums';
import { PaymentDeviceDetailsDtoIn, PaymentDeviceDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import {
  MANAGEMENT_DEVICES_URL,
  PaymentDeviceAction,
  allowedDeviceActions,
  formatDeviceDate,
} from '@/helpers/paymentDevices';
import { DeviceStatusChip } from './DeviceStatusChip';
import { RentalRow } from './RentalRows';
import RentalHistoryTable from './RentalHistoryTable';
import styles from '../FundsManagement/FundBlade/FundBlade.module.css';

interface DeviceBladeProps {
  deviceId: number;
  onClose: () => void;
  onAction: (action: PaymentDeviceAction, device: PaymentDeviceDtoIn) => void;
}

const formatTimestamp = (value: string) => new Date(value).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' });

/** The log opens on its latest entries; the rest is one click away. */
const EVENTS_SHOWN = 5;

/** A device's card: its details and actions, every rental it had, and the log of what was done to it. */
const DeviceBlade = ({ deviceId, onClose, onAction }: DeviceBladeProps) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetAuthApi<PaymentDeviceDetailsDtoIn>(`${MANAGEMENT_DEVICES_URL}/${deviceId}`);
  const device = data?.device;
  const [showAllEvents, setShowAllEvents] = useState(false);
  const isOpen = (status: PaymentDeviceRentalStatus) =>
    status === PaymentDeviceRentalStatus.Reserved || status === PaymentDeviceRentalStatus.Active;
  const openRentals = (data?.rentals ?? []).filter((rental) => isOpen(rental.status));
  const pastRentals = (data?.rentals ?? []).filter((rental) => !isOpen(rental.status));
  const events = data?.events ?? [];
  const shownEvents = showAllEvents ? events : events.slice(0, EVENTS_SHOWN);

  const headerTop = (
    <div className="flex items-center gap-3">
      <span className="body-lr-semibold text-(--font-body-4)" dir="ltr">{device?.deviceNumber ?? ''}</span>
    </div>
  );

  return (
    <Blade handleXclick={onClose} headerTop={headerTop} headerBottom={<></>} width="520px">
      <div className={`${styles.content} custom-scrollbar px-6 pb-6`}>
        {isLoading || !data || !device ? (
          <p className={styles.emptyText}>{t('loading')}...</p>
        ) : (
          <>
            <section className="flex flex-col gap-3">
              <h3 className={styles.sectionTitle}>{t('paymentDevices.blade.details')}</h3>
              <div className="flex items-center gap-3 flex-wrap">
                <DeviceStatusChip status={device.status} />
                <span className={styles.personMeta}>
                  {t('paymentDevices.blade.createdAt')}: {formatDeviceDate(device.createdAt)}
                </span>
              </div>
              {device.notes && <p className="body-sm-regular whitespace-pre-line">{device.notes}</p>}
              <div className="flex flex-wrap gap-2">
                {allowedDeviceActions(device).map((action) => (
                  <button key={action} type="button" className={styles.rowEditBtn} onClick={() => onAction(action, device)}>
                    {t(`paymentDevices.actions.${action}`)}
                  </button>
                ))}
              </div>
            </section>

            <div className={styles.line} />

            <section className="flex flex-col gap-3">
              <h3 className={styles.sectionTitle}>{t('paymentDevices.blade.rentals')}</h3>
              {data.rentals.length === 0 ? (
                <p className={styles.emptyText}>{t('paymentDevices.blade.noRentals')}</p>
              ) : (
                <>
                  {openRentals.length > 0 && (
                    <div className={styles.personList}>
                      {openRentals.map((rental) => (
                        <RentalRow
                          key={rental.id}
                          rental={rental}
                          heading={t('paymentDevices.blade.rentalLine', { fundId: rental.fundId, fundName: rental.fundName ?? '' })}
                        />
                      ))}
                    </div>
                  )}
                  {pastRentals.length > 0 && <RentalHistoryTable rentals={pastRentals} by="fund" />}
                </>
              )}
            </section>

            <div className={styles.line} />

            <section className="flex flex-col gap-3">
              <h3 className={styles.sectionTitle}>{t('paymentDevices.blade.events')}</h3>
              {events.length === 0 ? (
                <p className={styles.emptyText}>{t('paymentDevices.blade.noEvents')}</p>
              ) : (
                <div className={styles.personList}>
                  {shownEvents.map((event) => (
                    <div key={event.id} className={styles.personRow}>
                      <span className="body-sm-semibold">
                        {t(`paymentDevices.eventType.${event.eventType}`)}
                        {event.fundId ? ` · ${t('paymentDevices.blade.rentalLine', { fundId: event.fundId, fundName: '' })}` : ''}
                      </span>
                      <span className={styles.personMeta}>
                        <span>{formatTimestamp(event.at)}</span>
                        {event.byEmail && (
                          <>
                            <span className={styles.metaDot}>•</span>
                            <span dir="ltr">{event.byEmail}</span>
                          </>
                        )}
                      </span>
                      {event.note && <span className={styles.personMeta}>{event.note}</span>}
                    </div>
                  ))}
                  {events.length > EVENTS_SHOWN && (
                    <button type="button" className={`${styles.rowEditBtn} self-start`} onClick={() => setShowAllEvents((value) => !value)}>
                      {showAllEvents ? t('paymentDevices.blade.showFewerEvents') : t('paymentDevices.blade.showAllEvents', { count: events.length })}
                    </button>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </Blade>
  );
};

export default DeviceBlade;
